// client/src/components/admin/AdminProposalsView.js
import React, { useEffect, useState, useCallback } from 'react'
import PageLayout from '../common/layout/PageLayout'
import useAuth from '../../hooks/useAuth'
import ApiAdminTools from '../../api/ApiAdminTools'
import ApiLeagues from '../../api/ApiLeagues'
import toastr from 'toastr'
import './AdminProposalsView.css'

function formatDateTime(dateTimeStr) {
  if (!dateTimeStr) return '—'
  const date = new Date(dateTimeStr)
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

function getStatusBadgeClass(status) {
  switch (status) {
    case 'pending':
      return 'badge bg-warning text-dark'
    case 'accepted':
      return 'badge bg-success'
    case 'declined':
      return 'badge bg-danger'
    case 'expired':
      return 'badge bg-secondary'
    case 'canceled':
    case 'dismissed':
      return 'badge bg-secondary'
    default:
      return 'badge bg-secondary'
  }
}

function AdminProposalsView() {
  const { isAdmin, loading: authLoading } = useAuth()
  const [proposals, setProposals] = useState([])
  const [totalProposals, setTotalProposals] = useState(0)
  const [leagues, setLeagues] = useState([])
  const [divisions, setDivisions] = useState([])
  const [divisionsLoading, setDivisionsLoading] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [leagueError, setLeagueError] = useState(null)
  const [lastRefresh, setLastRefresh] = useState(null)

  // Filters
  const [dateRangeFilter, setDateRangeFilter] = useState('future') // 'future', 'next7', 'next30', 'all'
  const [leagueFilter, setLeagueFilter] = useState('all')
  const [divisionFilter, setDivisionFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')

  // Sorting
  const [sortColumn, setSortColumn] = useState('proposed_time')
  const [sortDirection, setSortDirection] = useState('asc')

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 50

  // Fetch leagues for filter dropdown
  useEffect(() => {
    async function fetchLeagues() {
      try {
        const data = await ApiLeagues.getAll()
        setLeagues(data || [])
        setLeagueError(null)
      } catch (err) {
        console.error('Failed to fetch leagues:', err)
        setLeagueError('Failed to load leagues for filter')
      }
    }
    if (isAdmin) {
      fetchLeagues()
    }
  }, [isAdmin])

  // Fetch divisions when league changes
  useEffect(() => {
    async function fetchDivisions() {
      if (leagueFilter === 'all') {
        setDivisions([])
        setDivisionFilter('all')
        setDivisionsLoading(false)
        return
      }

      setDivisionsLoading(true)
      try {
        const selectedLeague = leagues.find(
          (league) => league.id === parseInt(leagueFilter, 10),
        )
        if (selectedLeague?.divisions) {
          setDivisions(selectedLeague.divisions)
        }
      } catch (err) {
        console.error('Failed to fetch divisions:', err)
      } finally {
        setDivisionsLoading(false)
      }
    }
    fetchDivisions()
  }, [leagueFilter, leagues])

  const fetchProposals = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const options = {
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
        sortBy: sortColumn,
        sortDirection: sortDirection,
      }

      if (leagueFilter !== 'all') {
        options.leagueId = parseInt(leagueFilter, 10)
      }

      if (divisionFilter !== 'all') {
        options.divisionId = parseInt(divisionFilter, 10)
      }

      if (statusFilter !== 'all') {
        options.status = statusFilter
      }

      if (dateRangeFilter === 'future') {
        const now = new Date()
        options.startDate = now.toISOString()
      } else if (dateRangeFilter === 'next7') {
        const now = new Date()
        const next7 = new Date()
        next7.setDate(now.getDate() + 7)
        options.startDate = now.toISOString()
        options.endDate = next7.toISOString()
      } else if (dateRangeFilter === 'next30') {
        const now = new Date()
        const next30 = new Date()
        next30.setDate(now.getDate() + 30)
        options.startDate = now.toISOString()
        options.endDate = next30.toISOString()
      }

      if (searchQuery.trim()) {
        options.search = searchQuery.trim()
      }

      const response = await ApiAdminTools.getAllMatchProposals(options)
      setProposals(response.proposals || [])
      setTotalProposals(response.total || 0)
      setLastRefresh(new Date())
    } catch (err) {
      console.error('Failed to fetch proposals:', err)
      setError('Failed to load proposals. Please try again.')
      toastr.error('Failed to load match proposals')
    } finally {
      setLoading(false)
    }
  }, [
    currentPage,
    dateRangeFilter,
    divisionFilter,
    leagueFilter,
    searchQuery,
    sortColumn,
    sortDirection,
    statusFilter,
  ])

  useEffect(() => {
    if (isAdmin && !authLoading) {
      fetchProposals()
    }
  }, [isAdmin, authLoading, fetchProposals])

  const totalPages = Math.max(1, Math.ceil(totalProposals / itemsPerPage))

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
    setCurrentPage(1)
  }

  const getAriaSortValue = (column) => {
    if (sortColumn !== column) return 'none'
    return sortDirection === 'asc' ? 'ascending' : 'descending'
  }

  const getSortIcon = (column) => {
    if (sortColumn !== column) return null
    const direction = sortDirection === 'asc' ? 'ascending' : 'descending'
    const icon = sortDirection === 'asc' ? '▲' : '▼'
    return (
      <span aria-label={`Sorted ${direction}`} role="img">
        {icon}
      </span>
    )
  }

  const getSortableHeaderProps = (column) => ({
    className: 'admin-proposals-sortable-header',
    onClick: () => handleSort(column),
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        handleSort(column)
      }
    },
    role: 'button',
    tabIndex: 0,
    'aria-sort': getAriaSortValue(column),
  })

  const renderPlayerCell = (proposal, type) => {
    const nameKey =
      type === 'proposer'
        ? `${proposal.proposer_first_name || ''} ${proposal.proposer_last_name || ''}`
        : `${proposal.opponent_first_name || ''} ${proposal.opponent_last_name || ''}`
    const emailKey =
      type === 'proposer' ? proposal.proposer_email : proposal.opponent_email

    return (
      <div>
        <div className="admin-proposals-player-name">
          {nameKey.trim() || '—'}
        </div>
        <div className="admin-proposals-player-email">{emailKey || '—'}</div>
      </div>
    )
  }

  if (authLoading) {
    return (
      <PageLayout title="Match Proposals">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </PageLayout>
    )
  }

  if (!isAdmin) {
    return (
      <PageLayout title="Match Proposals">
        <div className="text-center py-5">
          <p className="text-danger">
            Access denied. Admin privileges required.
          </p>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout title="Match Proposals">
      <div className="container py-4">
        <div className="admin-proposals-container">
          <div className="admin-proposals-header mb-3">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
              <div>
                <h4 className="mb-1">All Match Proposals</h4>
                <p className="text-muted mb-0">
                  {totalProposals} proposal
                  {totalProposals !== 1 ? 's' : ''} total
                </p>
                {lastRefresh && (
                  <small className="text-muted">
                    Last updated: {lastRefresh.toLocaleTimeString()}
                  </small>
                )}
              </div>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={fetchProposals}
              >
                Refresh
              </button>
            </div>
          </div>

          <div className="admin-proposals-filters mb-4">
            <div className="row g-3">
              <div className="col-12 col-md-3">
                <label className="form-label">League</label>
                <select
                  className="form-select"
                  value={leagueFilter}
                  onChange={(e) => {
                    setLeagueFilter(e.target.value)
                    setCurrentPage(1)
                  }}
                >
                  <option value="all">All Leagues</option>
                  {leagues.map((league) => (
                    <option key={league.id} value={league.id}>
                      {league.name}
                    </option>
                  ))}
                </select>
                {leagueError && (
                  <div className="text-danger small mt-1">{leagueError}</div>
                )}
              </div>
              <div className="col-12 col-md-3">
                <label className="form-label">Division</label>
                <select
                  className="form-select"
                  value={divisionFilter}
                  onChange={(e) => {
                    setDivisionFilter(e.target.value)
                    setCurrentPage(1)
                  }}
                  disabled={leagueFilter === 'all' || divisionsLoading}
                >
                  <option value="all">
                    {divisionsLoading
                      ? 'Loading divisions...'
                      : 'All Divisions'}
                  </option>
                  {divisions.map((division) => (
                    <option key={division.id} value={division.id}>
                      {division.division_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12 col-md-3">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value)
                    setCurrentPage(1)
                  }}
                >
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="accepted">Accepted</option>
                  <option value="declined">Declined</option>
                  <option value="expired">Expired</option>
                  <option value="canceled">Canceled</option>
                  <option value="dismissed">Dismissed</option>
                </select>
              </div>
              <div className="col-12 col-md-3">
                <label className="form-label">Date Range</label>
                <select
                  className="form-select"
                  value={dateRangeFilter}
                  onChange={(e) => {
                    setDateRangeFilter(e.target.value)
                    setCurrentPage(1)
                  }}
                >
                  <option value="future">Future Only</option>
                  <option value="next7">Next 7 Days</option>
                  <option value="next30">Next 30 Days</option>
                  <option value="all">All Dates</option>
                </select>
              </div>
              <div className="col-12 col-md-4">
                <label className="form-label">Search</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search player, email, league..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value)
                    setCurrentPage(1)
                  }}
                />
              </div>
            </div>
          </div>

          {loading && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}

          {!loading && error && (
            <div className="alert alert-danger">{error}</div>
          )}

          {!loading && !error && proposals.length === 0 && (
            <div className="admin-proposals-empty-state">
              <i className="bi bi-inbox" />
              <h5>No Match Proposals</h5>
              <p>There are no match proposals that match these filters.</p>
            </div>
          )}

          {!loading && !error && proposals.length > 0 && (
            <>
              <div className="admin-proposals-table-container">
                <table className="table admin-proposals-table">
                  <thead>
                    <tr>
                      <th {...getSortableHeaderProps('proposed_time')}>
                        Proposed Time
                        <span className="admin-proposals-sort-indicator">
                          {getSortIcon('proposed_time')}
                        </span>
                      </th>
                      <th {...getSortableHeaderProps('expires_at')}>
                        Expires
                        <span className="admin-proposals-sort-indicator">
                          {getSortIcon('expires_at')}
                        </span>
                      </th>
                      <th {...getSortableHeaderProps('status')}>
                        Status
                        <span className="admin-proposals-sort-indicator">
                          {getSortIcon('status')}
                        </span>
                      </th>
                      <th {...getSortableHeaderProps('proposer_name')}>
                        Proposer
                        <span className="admin-proposals-sort-indicator">
                          {getSortIcon('proposer_name')}
                        </span>
                      </th>
                      <th {...getSortableHeaderProps('opponent_name')}>
                        Opponent
                        <span className="admin-proposals-sort-indicator">
                          {getSortIcon('opponent_name')}
                        </span>
                      </th>
                      <th {...getSortableHeaderProps('league_name')}>
                        League
                        <span className="admin-proposals-sort-indicator">
                          {getSortIcon('league_name')}
                        </span>
                      </th>
                      <th {...getSortableHeaderProps('division_name')}>
                        Division
                        <span className="admin-proposals-sort-indicator">
                          {getSortIcon('division_name')}
                        </span>
                      </th>
                      <th {...getSortableHeaderProps('created_at')}>
                        Created
                        <span className="admin-proposals-sort-indicator">
                          {getSortIcon('created_at')}
                        </span>
                      </th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {proposals.map((proposal) => (
                      <tr key={proposal.id}>
                        <td className="admin-proposals-cell-datetime">
                          {formatDateTime(proposal.proposed_time)}
                        </td>
                        <td className="admin-proposals-cell-datetime">
                          {formatDateTime(proposal.expires_at)}
                        </td>
                        <td className="admin-proposals-cell-status">
                          <span
                            className={getStatusBadgeClass(proposal.status)}
                          >
                            {proposal.status || 'unknown'}
                          </span>
                        </td>
                        <td className="admin-proposals-cell-player">
                          {renderPlayerCell(proposal, 'proposer')}
                        </td>
                        <td className="admin-proposals-cell-player">
                          {renderPlayerCell(proposal, 'opponent')}
                        </td>
                        <td className="admin-proposals-cell-league">
                          {proposal.league_name || '—'}
                        </td>
                        <td className="admin-proposals-cell-division">
                          {proposal.division_name || '—'}
                        </td>
                        <td className="admin-proposals-cell-datetime">
                          {formatDateTime(proposal.created_at)}
                        </td>
                        <td className="admin-proposals-cell-notes">
                          {proposal.notes || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                <div className="text-muted small">
                  Page {currentPage} of {totalPages || 1} ({totalProposals}{' '}
                  total)
                </div>
                <nav aria-label="Match proposals pagination">
                  <ul className="pagination pagination-sm mb-0">
                    <li
                      className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}
                    >
                      <button
                        className="page-link"
                        onClick={() =>
                          setCurrentPage(Math.max(1, currentPage - 1))
                        }
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                    </li>
                    <li
                      className={`page-item ${currentPage >= totalPages ? 'disabled' : ''}`}
                    >
                      <button
                        className="page-link"
                        onClick={() =>
                          setCurrentPage(Math.min(totalPages, currentPage + 1))
                        }
                        disabled={currentPage >= totalPages}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </>
          )}
        </div>
      </div>
    </PageLayout>
  )
}

export default AdminProposalsView
